// Starter written safety programs for the Safety Documents "Safety Policies"
// bucket. These are STARTING POINTS aligned to OSHA/EPA references — they must
// be reviewed and tailored by Titan's competent person / safety advisor before
// use, and are not a substitute for legal or professional compliance advice.
//
// Each entry becomes an editable, signable Safety Policy when added from the
// library. Body is plain text (rendered with preserved line breaks).

export const SAFETY_DISCLAIMER =
  'This is a starter template aligned to referenced OSHA/EPA standards. It must be reviewed and tailored by a competent person and Titan’s safety advisor to match actual site conditions and company practice. It is not legal advice or a certified compliance document.';

const f = (body) => `${body.trim()}\n\n———\n${SAFETY_DISCLAIMER}`;

export const SAFETY_POLICY_LIBRARY = [
  {
    key: 'heat_illness',
    title: 'Heat Illness Prevention Program',
    category: 'Safety Policies',
    summary: 'Water, rest, shade, acclimatization, monitoring & emergency response for hot-weather work.',
    body: f(`HEAT ILLNESS PREVENTION PROGRAM

PURPOSE
Protect employees from heat-related illness during work in hot indoor or outdoor environments. Aligned to the OSHA General Duty Clause (Section 5(a)(1)) and applicable state heat-illness rules.

SCOPE
Applies to all Titan employees performing work when the heat index reaches or exceeds 80°F, and to any work with high exertion, direct sun, or heavy PPE.

RESPONSIBILITIES
• Supervisors monitor conditions, enforce water/rest/shade, and start emergency response.
• Employees drink water regularly, take rest breaks, and report symptoms early.
• The safety coordinator tracks the daily heat index and adjusts work as needed.

WATER
• Provide cool, potable drinking water — enough for at least 1 quart per employee per hour (about one 8 oz cup every 15–20 minutes).
• Keep water close to the work area and refill before it runs out.

REST & SHADE
• Provide access to shade (or A/C) at all times when the heat index is 80°F or higher.
• Encourage preventive cool-down rest breaks; never deny a break to a worker who feels overheated.
• Add scheduled breaks and lighten workloads as the heat index rises (high-heat procedures at 90°F+).

ACCLIMATIZATION
• New and returning workers build up heat tolerance gradually (e.g., 20% exposure day one, +20% each day).
• Watch new workers closely — many heat fatalities happen in the first days on the job.

MONITORING & BUDDY SYSTEM
• Use a buddy system in high heat; observe each other for symptoms.
• More frequent check-ins during a heat wave or when the index is 90°F+.

RECOGNIZING & RESPONDING
• Heat exhaustion: heavy sweating, weakness, dizziness, nausea, headache — move to shade, cool, hydrate, do not leave alone.
• Heat stroke (EMERGENCY): confusion, slurred speech, hot/dry or wet skin, collapse, seizures — CALL 911, move to shade, cool aggressively (ice/water), stay with the worker.

TRAINING
Train all employees and supervisors before hot-season work on risk factors, prevention, symptoms, emergency response, and this program.

COMPANY DETAILS TO CONFIRM
Designated safety coordinator, shade/cooling locations per site, emergency contacts, and the state heat rule that applies to your jobsite.`),
  },
  {
    key: 'spill_prevention',
    title: 'Spill Prevention & Response Program',
    category: 'Safety Policies',
    summary: 'Prevent, contain, clean up, and report fuel, oil, hydraulic fluid & drilling-fluid spills.',
    body: f(`SPILL PREVENTION & RESPONSE PROGRAM

PURPOSE
Prevent releases of fuels, oils, hydraulic fluid, and drilling fluids; contain and clean up spills promptly; and report them properly. References EPA SPCC (40 CFR 112) for oil, OSHA Hazard Communication (29 CFR 1910.1200), and HAZWOPER (29 CFR 1910.120) for cleanup.

SCOPE
Applies to fueling, equipment servicing, storage of petroleum products and chemicals, and directional-drilling fluid returns on all Titan operations.

RESPONSIBILITIES
• Supervisors ensure spill kits are stocked, staff are trained, and spills are reported.
• Employees fuel/service carefully, inspect for leaks, and respond to small spills.
• Only trained personnel attempt cleanup within their training level.

PREVENTION
• Inspect equipment, hoses, and containers for leaks daily; tag out leaking equipment.
• Use drip pans and secondary containment under stationary tanks, generators, and fuel storage.
• Keep containers closed, labeled, and stored away from drains, waterways, and traffic.
• Never top off tanks; attend fueling at all times; use spill-proof nozzles.
• Contain and manage drilling-fluid returns; do not allow slurry to enter storm drains or waterways.

SPILL KITS
• Keep a stocked spill kit (absorbent pads/socks, granular absorbent, bags, gloves, goggles) on fuel trucks, drill rigs, and at storage areas.
• Restock immediately after any use.

RESPONSE (SMALL / INCIDENTAL SPILL)
1. Stop the source safely (shut valve, upright the container).
2. Protect drains and waterways — dike/berm with absorbent.
3. Contain and absorb; place used absorbent in labeled waste bags.
4. Notify your supervisor.

RESPONSE (LARGE / UNCONTROLLED RELEASE — EMERGENCY)
• Evacuate and secure the area; eliminate ignition sources.
• CALL 911 and notify the supervisor and safety coordinator.
• Do NOT attempt cleanup beyond your training — use trained responders.

REPORTING
• Report every spill to your supervisor, including small ones.
• Report to the National Response Center (1-800-424-8802) and state agency as required for reportable quantities to water/land.
• Document what, where, how much, cause, cleanup, and disposal.

WASTE DISPOSAL
Dispose of contaminated absorbent and soil as required for the material; keep manifests/receipts.

COMPANY DETAILS TO CONFIRM
Spill kit locations, reportable quantities, disposal vendor, and the SPCC plan if aggregate oil storage triggers 40 CFR 112.`),
  },
  {
    key: 'hazcom',
    title: 'Hazard Communication Program (HazCom / GHS)',
    category: 'Safety Policies',
    summary: 'Chemical inventory, Safety Data Sheets, GHS labels & employee right-to-know training.',
    body: f(`HAZARD COMMUNICATION PROGRAM (HazCom / GHS)

PURPOSE
Ensure employees are informed about the hazardous chemicals they work with. Complies with OSHA Hazard Communication Standard, 29 CFR 1910.1200 (adopted for construction by 1926.59).

SCOPE
Applies to all hazardous chemicals known to be present in Titan operations — fuels, solvents, adhesives, PVC cement, sealants, cleaners, and any product with an SDS.

RESPONSIBILITIES
• The HazCom coordinator maintains the written program, chemical inventory, and SDS library.
• Supervisors ensure containers are labeled and SDSs are accessible on site.
• Employees follow labels and SDS precautions and report unlabeled containers.

CHEMICAL INVENTORY
• Maintain a current list of hazardous chemicals used/stored; update as products change.

SAFETY DATA SHEETS (SDS)
• Keep an SDS for every hazardous chemical; make them readily accessible to employees on every shift (paper or electronic).
• Request SDSs from suppliers when missing.

LABELS
• Keep manufacturer GHS labels intact (product identifier, signal word, pictograms, hazard/precautionary statements).
• Label every secondary/workplace container with the product identity and hazards.

TRAINING
Train employees at initial assignment and when a new hazard is introduced on: the standard, hazards of chemicals in their area, how to read labels and SDSs, protective measures, and the location of the written program and SDSs.

NON-ROUTINE TASKS & MULTI-EMPLOYER SITES
Communicate hazards for non-routine tasks and share hazard information with other contractors on shared sites.

COMPANY DETAILS TO CONFIRM
HazCom coordinator name, SDS access method (binder/app), and the current chemical inventory location.`),
  },
  {
    key: 'ppe',
    title: 'Personal Protective Equipment (PPE) Program',
    category: 'Safety Policies',
    summary: 'Hazard assessment, required PPE by task, use, care & training.',
    body: f(`PERSONAL PROTECTIVE EQUIPMENT (PPE) PROGRAM

PURPOSE
Protect employees from hazards that engineering and work-practice controls cannot fully eliminate. References OSHA 1926 Subpart E (construction PPE) and 1910.132.

SCOPE
Applies to all Titan employees and covers head, eye/face, hearing, hand, foot, high-visibility, respiratory, and fall-protection PPE.

HAZARD ASSESSMENT
• A competent person assesses each task for hazards and selects the required PPE.
• Reassess when tasks, equipment, or conditions change.

MINIMUM SITE PPE
• Hard hat, safety glasses (ANSI Z87.1), and sturdy work boots on active jobsites.
• High-visibility apparel (ANSI/ISEA 107) in roadways and work zones.

TASK-BASED PPE
• Hearing protection where noise is high (drilling, saws, jackhammers).
• Cut/impact gloves for material handling; insulating gloves for energized electrical work.
• Face shield/goggles for cutting, grinding, chipping, and chemical handling.
• Arc-rated PPE for electrical work per NFPA 70E incident-energy analysis.
• Respirators only under the separate Respiratory Protection Program (medical eval, fit test, training).

USE & CARE
• Inspect PPE before each use; remove damaged PPE from service.
• Store PPE properly; replace per manufacturer guidance.
• Titan provides required PPE at no cost (except ordinary safety-toe boots/prescription eyewear where allowed).

TRAINING
Train employees on when PPE is needed, what to use, how to don/doff/adjust, limitations, and care. Retrain when the workplace or PPE changes.

COMPANY DETAILS TO CONFIRM
Written hazard assessment certification, PPE issuance records, and the respirator program if respirators are used.`),
  },
  {
    key: 'loto',
    title: 'Control of Hazardous Energy (Lockout/Tagout)',
    category: 'Safety Policies',
    summary: 'Isolate and verify zero energy before servicing equipment or circuits.',
    body: f(`CONTROL OF HAZARDOUS ENERGY (LOCKOUT/TAGOUT)

PURPOSE
Prevent unexpected energization or release of stored energy during service and maintenance. References OSHA 1910.147 and construction electrical LOTO 1926.417.

SCOPE
Applies to servicing/maintenance of machinery, equipment, and electrical circuits where unexpected startup or stored energy (electrical, hydraulic, pneumatic, mechanical, gravity) could cause injury.

RESPONSIBILITIES
• Authorized employees apply and remove locks/tags and verify de-energization.
• Affected employees are notified and stay clear until work is complete.
• Supervisors ensure procedures and devices are available and followed.

LOCKOUT PROCEDURE
1. Notify all affected employees.
2. Shut down equipment using normal stopping procedure.
3. Isolate every energy source (disconnects, valves).
4. Apply a lock and tag to each isolation point — one lock per authorized worker.
5. Release/restrain stored energy (discharge capacitors, bleed pressure, block raised parts).
6. VERIFY zero energy by testing controls and using a meter (test-before-touch) — return controls to off.

GROUP & SHIFT
• Use a group lockout device so each worker applies a personal lock.
• Hand off locks properly at shift change so protection is continuous.

REMOVAL
Only the worker who applied a lock removes it, after tools are clear, guards are back, and employees are notified. Follow the documented exception procedure if an owner is unavailable.

ELECTRICAL
For electrical work, de-energize whenever feasible, apply LOTO, verify with a meter, and apply protective grounds where required.

TRAINING
Train authorized and affected employees; retrain when procedures or equipment change.

COMPANY DETAILS TO CONFIRM
Equipment-specific procedures, lock/tag issuance, and the authorized-employee list.`),
  },
  {
    key: 'electrical_arc_flash',
    title: 'Electrical Safety & Arc Flash (NFPA 70E)',
    category: 'Safety Policies',
    summary: 'Qualified persons, approach boundaries, energized-work permits & arc-rated PPE.',
    body: f(`ELECTRICAL SAFETY & ARC FLASH PROGRAM

PURPOSE
Protect employees from shock, arc flash, and arc blast. References OSHA 1926 Subpart K (construction) and 1910.331–.335, using NFPA 70E as consensus practice.

SCOPE
Applies to all work on or near electrical conductors and equipment, including street lighting, traffic-signal, and service work.

QUALIFIED vs. UNQUALIFIED
• Only qualified persons — trained on hazards, equipment, and safe procedures — work on or near exposed energized parts.
• Unqualified persons stay outside the limited approach boundary.

DE-ENERGIZE FIRST
• Establish an electrically safe work condition (de-energize + LOTO + verify) whenever feasible.
• Energized work is allowed only when de-energizing introduces greater hazard or is infeasible — with an energized electrical work permit and justification.

APPROACH BOUNDARIES
• Maintain shock approach boundaries (limited/restricted) for the voltage involved.
• Establish the arc-flash boundary from an incident-energy analysis or table method.

ARC-RATED PPE & TOOLS
• Wear arc-rated clothing/PPE matched to the incident energy (cal/cm²); no meltable synthetics underneath.
• Use insulated/insulating tools, rubber insulating gloves with leather protectors (inspected/tested), and voltage-rated meters.

TEST BEFORE TOUCH
Verify absence of voltage with a tested meter before contact; treat all conductors as energized until proven otherwise.

OVERHEAD & UNDERGROUND
• Maintain minimum approach distances from overhead lines for people and equipment; use spotters.
• Locate and pothole underground utilities before excavation/drilling.

TRAINING
Train and periodically retrain qualified persons; document qualifications.

COMPANY DETAILS TO CONFIRM
Incident-energy study/labeling, glove test schedule, and the qualified-person roster.`),
  },
  {
    key: 'excavation_trenching',
    title: 'Excavation & Trenching Safety',
    category: 'Safety Policies',
    summary: 'Protective systems, competent-person inspections, access/egress & utility locating.',
    body: f(`EXCAVATION & TRENCHING SAFETY PROGRAM

PURPOSE
Prevent cave-ins and related hazards during excavation and trenching. References OSHA 1926 Subpart P.

SCOPE
Applies to all Titan excavation, trenching, potholing, and directional-boring pit work.

COMPETENT PERSON
A designated competent person classifies soil, selects protective systems, and inspects daily — with authority to stop work and correct hazards.

BEFORE DIGGING
• Call 811 and have utilities located and marked; pothole/hand-dig within the tolerance zone.
• Identify overhead lines and surface hazards.

PROTECTIVE SYSTEMS (trenches 5 ft+; and shallower if a competent person sees a hazard)
• Slope, bench, shore, or shield per soil classification and approved tables/engineering.
• Trenches 20 ft+ require a registered professional engineer’s design.

ACCESS & EGRESS
• Provide a ladder, ramp, or steps within 25 ft of lateral travel in trenches 4 ft+ deep.

SPOIL & LOADS
• Keep spoil piles, equipment, and materials at least 2 ft from the edge.
• Barricade or cover open excavations; protect the public and traffic.

HAZARDOUS ATMOSPHERE & WATER
• Test the atmosphere where >4 ft deep and a hazard could exist; ventilate as needed.
• Control water accumulation; a competent person re-inspects after rain or changing conditions.

DAILY INSPECTION
The competent person inspects before each shift, as conditions change, and after rain — workers stay out until the excavation is safe.

TRAINING
Train employees on hazards, protective systems, and this program; train competent persons to required depth.

COMPANY DETAILS TO CONFIRM
Competent-person names, soil-classification method, and shoring/shield equipment on hand.`),
  },
  {
    key: 'silica',
    title: 'Respirable Crystalline Silica Exposure Control',
    category: 'Safety Policies',
    summary: 'Dust controls for cutting/coring/grinding concrete, plus the Table 1 controls.',
    body: f(`RESPIRABLE CRYSTALLINE SILICA EXPOSURE CONTROL PLAN

PURPOSE
Limit employee exposure to respirable crystalline silica from concrete, masonry, and rock work. References OSHA 1926.1153.

SCOPE
Applies to cutting, coring, grinding, chipping, breaking, drilling, and dry sweeping of silica-containing materials.

CONTROLS (Table 1 / Specified Exposure Control Methods)
• Use water (wet cutting) to suppress dust, or a tool-mounted HEPA dust-collection shroud/vacuum, per Table 1 for each task.
• Operate and maintain controls per the manufacturer; keep water flowing / vacuums working.
• Where Table 1 isn’t fully followed, assess exposures and control below the PEL (50 µg/m³, 8-hr TWA).

RESPIRATORS
• Provide respirators as specified in Table 1 or when needed to meet the PEL — under the Respiratory Protection Program (medical eval, fit test, training).

WORK PRACTICES
• No dry sweeping/blowing where it exposes workers — use HEPA vacuum or wet methods.
• Limit access to high-dust areas to necessary workers.

HOUSEKEEPING & HYGIENE
• Keep dust off clothing; provide wash facilities; no eating/drinking in dusty areas.

MEDICAL SURVEILLANCE
Offer medical exams to employees required to wear a respirator 30+ days a year for silica.

COMPETENT PERSON
A competent person implements this plan and inspects controls.

TRAINING
Train employees on silica health effects, tasks that expose them, the controls used, and this plan.

COMPANY DETAILS TO CONFIRM
Competent-person name, the task/control table used on your equipment, and the medical-surveillance provider.`),
  },
  {
    key: 'fall_protection',
    title: 'Fall Protection Program',
    category: 'Safety Policies',
    summary: 'Guardrails, PFAS, and controls for work at 6 ft or more in construction.',
    body: f(`FALL PROTECTION PROGRAM

PURPOSE
Protect employees from falls to a lower level. References OSHA 1926 Subpart M (construction — 6 ft trigger) and 1926.451/.452 for scaffolds, 1926.453 for aerial lifts.

SCOPE
Applies to work at heights of 6 ft or more above a lower level, near excavations/holes, on scaffolds, aerial and scissor lifts, and elevated structures.

RESPONSIBILITIES
• A competent person selects fall-protection systems and inspects equipment/anchorages.
• Employees use provided systems correctly and inspect their gear before use.

PROTECTION METHODS
• Guardrails, safety nets, or personal fall arrest systems (PFAS) at 6 ft+.
• Cover or guard holes and skylights; guard the edges of excavations where required.

PERSONAL FALL ARREST (PFAS)
• Full-body harness, shock-absorbing lanyard or SRL, and a compliant anchorage.
• Anchorages rated 5,000 lb per worker (or engineered 2:1).
• Rig to limit free fall to 6 ft and prevent contact with a lower level; account for total fall clearance.

AERIAL / SCISSOR LIFTS
• Aerial (boom) lifts: wear a harness with lanyard attached to the manufacturer anchor.
• Scissor lifts: keep the guardrail system intact and stay inside the platform.

INSPECTION & RESCUE
• Inspect harnesses/lanyards/SRLs before each use; remove damaged or deployed gear.
• Have a prompt rescue plan for a suspended worker (do not rely on 911 alone).

TRAINING
A competent person trains employees on hazards, systems, use, inspection, and rescue; retrain as needed.

COMPANY DETAILS TO CONFIRM
Competent-person name, anchorage/engineering details, and the rescue plan/equipment.`),
  },
  {
    key: 'work_zone_traffic',
    title: 'Work Zone / Temporary Traffic Control Safety',
    category: 'Safety Policies',
    summary: 'Traffic control plans, high-vis, flagging & protecting crews from traffic.',
    body: f(`WORK ZONE / TEMPORARY TRAFFIC CONTROL SAFETY

PURPOSE
Protect employees and the public during work in or near roadways. References OSHA 1926 Subpart G, the MUTCD (Part 6), and applicable state/agency traffic-control requirements.

SCOPE
Applies to all Titan work in streets, shoulders, intersections, and traffic-signal/street-lighting operations affecting traffic.

TRAFFIC CONTROL PLAN (TCP)
• Use an approved TCP appropriate to the road, speed, and duration.
• Set up advance warning, transition (tapers), buffer, work, and termination areas per the MUTCD.

DEVICES
• Use compliant signs, cones, drums, barricades, and arrow boards; keep them clean, visible, and correctly spaced.
• Night work: retroreflective devices and adequate lighting without glare to drivers.

HIGH-VISIBILITY APPAREL
All workers exposed to traffic wear ANSI/ISEA 107 high-visibility apparel (Class 2/3 as conditions require).

FLAGGERS
• Trained/certified flaggers use STOP/SLOW paddles and standard signals.
• Provide flaggers an escape path and high-visibility clothing; position them safely.

INTERNAL TRAFFIC / EQUIPMENT
• Establish an internal traffic control plan separating workers from equipment.
• Use spotters for backing; equipment has working backup alarms; watch swing radius.
• Keep a positive buffer between traffic and crews; never turn your back to live traffic.

TRAINING
Train employees on the TCP, device setup/removal order, high-visibility requirements, and flagging (for flaggers).

COMPANY DETAILS TO CONFIRM
Agency/permit requirements per jurisdiction, flagger certifications, and approved TCP templates.`),
  },
];
